import { useEffect, useState } from 'react';
import WatchlistDashboardService, { StrategicList } from '../WatchlistDashboardService';
import StrategicCard from './StrategicCard';

/**
 * Layer 2 — Strategic Watchlists section (tabbed by outcome).
 *
 * Outcomes are added one per Stage-2 sub-step (hiring, then mentorship, …). Each outcome's 4
 * watchlist cards (core / conversion / breakthrough / reactivation) come from the per-watchlist
 * list endpoint (limit 5) — total = card count, alumni = top N. watchlist_code follows the locked
 * `<outcome>_<watchlist>` convention, so the codes are derived deterministically.
 */
export interface OutcomeTab {
    key: string;
    label: string; // includes emoji, e.g. "🎯 Hiring"
}

const WATCHLISTS = ['core', 'conversion', 'breakthrough', 'reactivation'];
const codesFor = (outcome: string) => WATCHLISTS.map((w) => `${outcome}_${w}`);
const TOP_N = 5;

const StrategicSection = ({
    outcomes,
    instituteId,
    filterInstituteId,
}: {
    outcomes: OutcomeTab[];
    instituteId: string | number;
    filterInstituteId?: string | null;
}) => {
    const [active, setActive] = useState(outcomes[0]?.key);
    const [cards, setCards] = useState<StrategicList[]>([]);
    const [outcomeTotals, setOutcomeTotals] = useState<Record<string, number>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Fetch counts for all outcomes (for tab badges)
    useEffect(() => {
        if (!outcomes.length) return;
        Promise.all(
            outcomes.map((o) =>
                Promise.all(
                    codesFor(o.key).map((code) =>
                        WatchlistDashboardService.getStrategicList(o.key, code, instituteId, { limit: 1, filterInstituteId })
                    )
                ).then((results) => ({
                    key: o.key,
                    total: results.reduce((sum, r) => sum + (r.total || 0), 0),
                }))
            )
        )
            .then((totals) => {
                const totalsMap: Record<string, number> = {};
                totals.forEach((t) => {
                    totalsMap[t.key] = t.total;
                });
                setOutcomeTotals(totalsMap);
            })
            .catch(() => {});
    }, [outcomes, instituteId, filterInstituteId]);

    // Fetch full cards data for active outcome
    useEffect(() => {
        if (!active) return;
        setLoading(true);
        setError(null);
        Promise.all(
            codesFor(active).map((code) =>
                WatchlistDashboardService.getStrategicList(active, code, instituteId, { limit: TOP_N, filterInstituteId })
            )
        )
            .then((results) => setCards(results))
            .catch((err) => setError(err?.message || 'Failed to load strategic watchlists.'))
            .finally(() => setLoading(false));
    }, [active, instituteId, filterInstituteId]);

    return (
        <div className="col-12">
            {/* Layer 2 header (mockup lines 281-289) */}
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-3 mb-3 mt-4 px-3 py-3 rounded-3 bg-purple-f5f3ff border-4 border-start border-bs-purple border-opacity-100">
                <span className="font-10 font-700 text-uppercase py-1 px-2 rounded-pill bg-bs-purple text-white bg-opacity-100">Layer 2</span>
                <span className="font-18 font-700 text-dark-blue">Strategic Watchlists</span>
                <span className="flex-grow-1 font-13 text-secondary-emphasis">
                    Decide where to focus — combination logic across Ability, Willingness &amp; Involvement signals
                </span>
                {!loading && !error && (
                    <span className="font-12 font-600 py-1 px-2 rounded-pill bg-purple-f5f3ff text-purple border border-bs-purple border-opacity-50">
                        {Object.values(outcomeTotals).reduce((sum, t) => sum + t, 0).toLocaleString()} alumni across watchlists
                    </span>
                )}
            </div>

            {/* Outcome tabs */}
            <div className="bg-white overflow-hidden rounded-top border border-bs-purple border-opacity-25">
                <div className="d-flex flex-wrap mb-0 border-bottom">
                    {outcomes.map((o) => {
                        const isActive = o.key === active;
                        const count = outcomeTotals[o.key] ?? 0;
                        return (
                            <div
                                key={o.key}
                                onClick={() => setActive(o.key)}
                                className={`px-4 py-3 text-center flex-fill flex-md-grow-0 font-13 font-600 cursor-pointer d-flex align-items-center justify-content-center gap-2 ${
                                    isActive
                                        ? 'text-dark border-bottom border-bs-purple border-2 border-opacity-100'
                                        : 'text-secondary-emphasis'
                                }`}>
                                {o.label}
                                <span className={`badge rounded-pill font-11 ${isActive ? 'bg-bs-purple text-white' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                                    {count}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="bg-white p-4 mb-4 border border-bs-purple border-opacity-25 border-top-0 rounded-bottom shadow-sm">
                {loading && <div className="font-14 text-secondary-emphasis py-2">Loading strategic watchlists…</div>}
                {error && !loading && (
                    <div className="alert alert-danger font-13" role="alert">
                        {error}
                    </div>
                )}
                {!loading && !error && (
                    <div className="row g-3 row-cols-1 row-cols-md-2">
                        {cards.map((card) => (
                            <StrategicCard key={card.watchlist_code} card={card} instituteId={instituteId} filterInstituteId={filterInstituteId} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default StrategicSection;
